import SectionDivider from 'components/shared/SectionDivider';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { ComponentProps, ReactNode, useState } from 'react';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import ProResponse from 'types/ProResponse';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import CreditCardForm from 'components/forms/CreditCardForm';
import CountryCode from 'types/CountryCode';
import TokenizedCreditCardForm, { CARD_VALIDATION_NAMES } from 'components/forms/TokenizedCreditCardForm';
import { TokenValidationErrors } from 'components/forms/TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import { CardElementState } from '@recurly/react-recurly';
import useExperiment from 'hooks/useExperiment';
import getFormattedCvvCode from './utils/getFormattedCvvCode';
import ApplePayButton from './ApplePayButton';
import ErrorText from './ErrorText';
import * as styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';
import GooglePayButton from './GooglePayButton';
import LoadingOverlay from './LoadingOverlay';

type Props = {
  billingCountry: CountryCode;
  coupon: Coupon;
  currencyCode: CurrencyCode;
  cvv: string;
  cvvError: ReactNode;
  errors: ProResponse['errors'];
  expiration: string;
  expirationError: ReactNode;
  name: string;
  nameError: ReactNode;
  number: string;
  numberError: ReactNode;
  onApplePayToken: ComponentProps<typeof ApplePayButton>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  postalCode: string;
  setName: (name: string) => void;
  setNumber: (number: string) => void;
  setPostalCode: (postalCode: string) => void;
  setCvv: (cvv: string) => void;
  setNumberError: (error: ReactNode) => void;
  setExpiration: (expiration: string) => void;
  setNameError: (error: ReactNode) => void;
  postalCodeError: ReactNode;
  setPostalCodeError: (error: ReactNode) => void;
  setErrors: (errors: ProResponse['errors']) => void;
  setCvvError: (error: ReactNode) => void;
  setExpirationError: (error: ReactNode) => void;
  multiyearConversion?: boolean;
  onBillingCountryChange: (countryCode: CountryCode, arg1: any) => void;
  setClientValidationErrors: (
    inputName: CARD_VALIDATION_NAMES,
    value: string | { number: CardElementState; expiry: CardElementState; cvv: CardElementState }
  ) => void;
  validationErrors: TokenValidationErrors;
};

export default function PaymentForm({
  billingCountry,
  coupon,
  currencyCode,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  errors,
  setErrors,
  expiration,
  number,
  cvv,
  postalCode,
  name,
  setExpiration,
  setNumber,
  setCvv,
  setPostalCode,
  setName,
  setNumberError,
  numberError,
  nameError,
  setNameError,
  setCvvError,
  cvvError,
  postalCodeError,
  setPostalCodeError,
  expirationError,
  setExpirationError,
  multiyearConversion,
  onBillingCountryChange,
  validationErrors,
  setClientValidationErrors
}: Props) {
  // this submitting state is used for non-creditcard services
  const [submitting, setSubmitting] = useState<boolean>(false);

  const ccTokenizationEnabled = useExperiment('web-growth-tokenize-recurly-11-23')?.value === 'treatment';

  function handleCreditCardFormChange(event: any) {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, {});

    switch (name) {
      case 'number': {
        /* 
          Handles the case where the user switches from an amex card number to any other card type, 
          which requires a different cvv character length.
        */
        const formattedCvv = getFormattedCvvCode(cvv, value);
        if (cvv !== formattedCvv) {
          setCvv(formattedCvv);
        }
        setNumber(value);
        break;
      }
      case 'expiration':
        setExpiration(value);
        break;
      case 'cvv': {
        const formattedCvv = getFormattedCvvCode(value, number);
        setCvv(formattedCvv);
        setCvvError(payload.errors.cvv);
        break;
      }
      case 'postalCode':
        setPostalCode(value);
        setPostalCodeError(payload.errors.postalCode);
        break;
      case 'name':
        setName(value);
        setNameError(payload.errors.name);
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  }

  return (
    <>
      <div className={styles.grid}>
        <ApplePayButton
          coupon={coupon}
          currencyCode={currencyCode}
          onApplePayToken={onApplePayToken}
          plan={plan}
          setErrors={setErrors}
          setSubmitting={setSubmitting}
        />
        <GooglePayButton
          coupon={coupon}
          currencyCode={currencyCode}
          onGooglePayToken={onGooglePayToken}
          plan={plan}
          setErrors={setErrors}
          setSubmitting={setSubmitting}
        />
        <PayPalButton
          coupon={coupon}
          onPayPalToken={onPayPalToken}
          plan={plan}
          setErrors={setErrors}
          setSubmitting={setSubmitting}
          submitting={submitting}
        />
      </div>
      <SectionDivider classes={styles.divider}>
        <FormattedMessage defaultMessage="Or enter payment details" />
      </SectionDivider>

      {ccTokenizationEnabled ? (
        <TokenizedCreditCardForm
          validationErrors={validationErrors}
          handleInputChange={(
            input: CARD_VALIDATION_NAMES,
            value: string | { number: CardElementState; expiry: CardElementState; cvv: CardElementState }
          ) => {
            if (errors) {
              setErrors(null);
            }
            setClientValidationErrors(input, value);
          }}
          billingCountry={{
            onChange: onBillingCountryChange,
            value: billingCountry
          }}
          isCouponInputExpanded={false}
          isCouponInputShown={false}
        />
      ) : (
        <CreditCardForm
          billingCountry={{
            onChange: onBillingCountryChange,
            value: billingCountry
          }}
          changeHandler={handleCreditCardFormChange}
          cvv={cvv}
          errors={{ expiration: expirationError, name: nameError, number: numberError, cvv: cvvError, postalCode: postalCodeError }}
          expiration={expiration}
          isCouponInputExpanded={false}
          isCouponInputShown={false}
          name={name}
          number={number}
          postalCode={postalCode}
          setError={(name, error) => {
            // these are then only two errors set on the credit card form
            switch (name) {
              case 'expiration':
                setExpirationError(error);
                break;
              case 'number':
                setNumberError(error);
                break;
              default:
                throw new Error(`missing credit card form error handling for name '${name}'`);
            }
          }}
        />
      )}
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
